import React, { forwardRef, useState, useEffect, useRef, useCallback } from 'react';
import { COLORS, TYPOGRAPHY } from '../../../styles/UISystem';
import SendButton from '../Buttons/SendButton';
import CameraButton from './CameraButton';

/**
 * ChatInputNormal Component
 *
 * Chat input with auto-expanding textarea and send button.
 * Send button color matches CHAT_SENT token.
 */

const MIN_HEIGHT = 50;
const MAX_HEIGHT = 150;

export const ChatInputNormal = forwardRef(
  (
    {
      value = '',
      onChange = () => {},
      onSend = () => {},
      onTakePhoto,
      placeholder = 'Message',
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState(value);
    const textareaRef = useRef(null);

    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    // Auto-resize textarea based on content
    const autoResize = useCallback(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.style.height = 'auto';
      const newHeight = Math.min(Math.max(textarea.scrollHeight, MIN_HEIGHT - 10), MAX_HEIGHT);
      textarea.style.height = `${newHeight}px`;
      textarea.style.overflowY = textarea.scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';
    }, []);

    useEffect(() => {
      autoResize();
    }, [internalValue, autoResize]);

    const handleChange = (e) => {
      const newValue = e.target.value;
      setInternalValue(newValue);
      onChange(newValue);
    };

    const handleSend = () => {
      if (internalValue.trim()) {
        onSend(internalValue);
        setInternalValue('');
        onChange('');
        // Reset textarea height after send
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
        if (document.activeElement) {
          document.activeElement.blur();
        }
      }
    };

    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && !e.shiftKey && internalValue.trim()) {
        e.preventDefault();
        handleSend();
      }
    };

    // Set both refs
    const setRefs = (element) => {
      textareaRef.current = element;
      if (ref) {
        if (typeof ref === 'function') {
          ref(element);
        } else {
          ref.current = element;
        }
      }
    };

    return (
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            minHeight: `${MIN_HEIGHT}px`,
            backgroundColor: 'white',
            border: `1px solid ${COLORS.MEDIUM_DARK_GRAY}`,
            borderRadius: '25px',
            padding: '5px 5px 5px 16px',
            boxSizing: 'border-box',
            position: 'relative',
          }}
        >
          <textarea
            ref={setRefs}
            value={internalValue}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            rows={1}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              backgroundColor: 'transparent',
              padding: '10px 0',
              resize: 'none',
              overflowY: 'hidden',
              lineHeight: '1.4',
              // Empty input renders light so the placeholder inherits a quiet
              // weight (user, 2026-07-19: it read too heavy); typed text is
              // regular. Value-driven, like FormConnectionNote — the old
              // `state` prop check never fired (no caller sets it).
              ...(internalValue
                ? {
                    ...TYPOGRAPHY.BODY_REGULAR,
                    color: COLORS.DARK_GRAY,
                  }
                : {
                    ...TYPOGRAPHY.BODY_LIGHT,
                    fontWeight: '300',
                  }),
            }}
            {...props}
          />
          {internalValue.trim() ? (
            <SendButton onClick={handleSend} />
          ) : onTakePhoto ? (
            <CameraButton onTakePhoto={onTakePhoto} />
          ) : null}
        </div>
      </div>
    );
  }
);

ChatInputNormal.displayName = 'ChatInputNormal';
